import { useMemo } from 'react';

interface AccessDetails {
  specific_service_access?: {
    service: string;
    accessLevel: 'while-alive' | 'after-death';
  }[];
  invitation_type?: 'CONSULTANT_ACCESS' | string;
  general_access_right?: 'while-alive' | 'after-death';
  has_specific_access?: boolean;
}

interface UseAccessParams {
  serviceAccessKeys: string[];
  accessDetails?: AccessDetails;
  userReportedDead: boolean;
}

const useServiceAccess = ({
  serviceAccessKeys,
  accessDetails,
  userReportedDead,
}: UseAccessParams): Record<string, boolean> =>
  useMemo(() => {
    // Early return if no services to check
    if (!serviceAccessKeys.length) {
      return {};
    }

    const specificAccessList = accessDetails?.specific_service_access || [];
    const generalAccess = accessDetails?.general_access_right;
    const hasSpecificAccess = accessDetails?.has_specific_access;
    const isConsultant = accessDetails?.invitation_type === 'CONSULTANT_ACCESS';

    // Helper to check if a service has specific access
    const getSpecificAccess = (serviceKey: string) =>
      specificAccessList.find((access) => access.service === serviceKey);

    // Helper to check if service exists in specific access
    const hasSpecificServiceAccess = (serviceKey: string): boolean =>
      specificAccessList.some((access) => access.service === serviceKey) ??
      false;

    // Helper to check tax service access
    const getTaxServiceAccess = (serviceKey: string): boolean => {
      if (!specificAccessList.length) {
        return false;
      }
      return hasSpecificServiceAccess(serviceKey);
    };

    // Helper to check consultant access
    const getConsultantAccess = (serviceKey: string): boolean => {
      if (!hasSpecificAccess) return true;
      return hasSpecificServiceAccess(serviceKey);
    };

    // Helper to check regular service access
    const getRegularServiceAccess = (serviceKey: string): boolean => {
      if (generalAccess === 'while-alive') {
        return (
          !hasSpecificAccess ||
          getSpecificAccess(serviceKey)?.accessLevel !== 'after-death'
        );
      }

      if (generalAccess === 'after-death') {
        if (!userReportedDead) {
          const specificAccess = getSpecificAccess(serviceKey);
          return (
            (hasSpecificAccess &&
              specificAccess?.accessLevel === 'while-alive') ||
            false
          );
        }
        return !hasSpecificAccess;
      }

      return false;
    };

    // Process all services
    return serviceAccessKeys.reduce<Record<string, boolean>>(
      (acc, serviceKey) => {
        const isTaxService = serviceKey.toLowerCase().includes('tax');

        let hasAccess: boolean;
        if (isConsultant) {
          hasAccess = getConsultantAccess(serviceKey);
        } else if (isTaxService) {
          hasAccess = getTaxServiceAccess(serviceKey);
        } else {
          hasAccess = getRegularServiceAccess(serviceKey);
        }

        return { ...acc, [serviceKey]: hasAccess };
      },
      {}
    );
  }, [serviceAccessKeys, accessDetails, userReportedDead]);

export default useServiceAccess;
